import type { PaxDocument } from '@optionway/models';
import { DateTime } from 'luxon';
import type { RefinementCtx } from 'zod';
import { z } from 'zod';

const errorPrefix = 'components.passengers.passportForm.errors';

// real passport validity is 10 years max; MAX_EXPIRY_YEARS adds a 5-year safety margin
// so only clearly wrong dates (e.g. a typo'd year) are rejected, not legitimate edge cases
const MAX_EXPIRY_YEARS = 15;

export interface PassportFormErrors {
  number?: string;
  expiryDate?: string;
  issuanceCountryCode?: string;
}

export const parsePassportDocument = (
  document: PaxDocument,
  now: string,
): { document?: PaxDocument; errors: PassportFormErrors } => {
  const result = buildPassportSchema(now).safeParse({
    number: document.number ?? '',
    expiryDate: document.expiryDate ?? '',
    issuanceCountryCode: document.issuanceCountry?.code ?? '',
  });
  if (!result.success) {
    const { fieldErrors } = result.error.flatten();
    return {
      errors: {
        number: fieldErrors.number?.[0],
        expiryDate: fieldErrors.expiryDate?.[0],
        issuanceCountryCode: fieldErrors.issuanceCountryCode?.[0],
      },
    };
  }
  return { document: { ...document }, errors: {} };
};

export const buildPassportSchema = (now: string) =>
  z
    .object({
      number: z.string().optional(),
      expiryDate: z.string().optional(),
      issuanceCountryCode: z.string().optional(),
    })
    .superRefine((data, context) => {
      checkPassportNumber(context, data.number);
      checkExpiryDate(context, now, data.expiryDate);
      checkIssuanceCountry(context, data.issuanceCountryCode);
    });

const checkPassportNumber = (context: RefinementCtx, number?: string): void => {
  if (number) return;
  context.addIssue({
    code: z.ZodIssueCode.custom,
    message: `${errorPrefix}.passportNumberRequired`,
    path: ['number'],
  });
};

const checkExpiryDate = (context: RefinementCtx, now: string, expiryDate?: string): void => {
  if (!expiryDate) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${errorPrefix}.expiryDateRequired`,
      path: ['expiryDate'],
    });
    return;
  }
  if (isExpired(expiryDate, now)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${errorPrefix}.expiryDateExpired`,
      path: ['expiryDate'],
    });
  }
  if (isTooFarInFuture(expiryDate, now)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${errorPrefix}.expiryDateTooFar`,
      path: ['expiryDate'],
    });
  }
};

const checkIssuanceCountry = (context: RefinementCtx, issuanceCountryCode?: string): void => {
  if (issuanceCountryCode) return;
  context.addIssue({
    code: z.ZodIssueCode.custom,
    message: `${errorPrefix}.issuanceCountryRequired`,
    path: ['issuanceCountryCode'],
  });
};

const isExpired = (expiryDate: string, now: string): boolean => {
  const expiry = DateTime.fromISO(expiryDate).startOf('day');
  const today = DateTime.fromISO(now).startOf('day');
  return expiry.isValid && today.isValid && expiry < today;
};

const isTooFarInFuture = (expiryDate: string, now: string): boolean => {
  const expiry = DateTime.fromISO(expiryDate).startOf('day');
  const limit = DateTime.fromISO(now).startOf('day').plus({ years: MAX_EXPIRY_YEARS });
  return expiry.isValid && limit.isValid && expiry > limit;
};
